import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to manage scroll position restoration
 * Saves and restores scroll positions for different routes/sections
 */
const useScrollPosition = () => {
    const scrollPositions = useRef(new Map());

    // Save current scroll position with a key
    const saveScrollPosition = useCallback((key = 'default') => {
        const position = {
            x: window.pageXOffset || document.documentElement.scrollLeft,
            y: window.pageYOffset || document.documentElement.scrollTop
        };
        scrollPositions.current.set(key, position);
        
        // Optional: Store in sessionStorage for persistence across page refreshes
        try {
            sessionStorage.setItem(`scroll_${key}`, JSON.stringify(position));
        } catch (error) {
            // Silently handle storage errors
            console.warn('Failed to save scroll position to sessionStorage:', error);
        }
    }, []);

    // Restore scroll position for a key
    const restoreScrollPosition = useCallback((key = 'default', options = {}) => {
        const {
            behavior = 'auto',  // 'smooth' or 'auto'
            fallbackSelector = null,  // CSS selector to fallback to if no saved position
            delay = 0  // Delay in ms before restoration
        } = options;

        const restore = () => {
            let position = scrollPositions.current.get(key);
            
            // Try to get from sessionStorage if not in memory
            if (!position) {
                try {
                    const stored = sessionStorage.getItem(`scroll_${key}`);
                    if (stored) {
                        position = JSON.parse(stored);
                    }
                } catch (error) {
                    console.warn('Failed to restore scroll position from sessionStorage:', error);
                }
            }

            if (position) {
                window.scrollTo({
                    left: position.x,
                    top: position.y,
                    behavior
                });
            } else if (fallbackSelector) {
                // Fallback to scrolling to a specific element
                const element = document.querySelector(fallbackSelector);
                if (element) {
                    element.scrollIntoView({ behavior });
                }
            }
        };

        if (delay > 0) {
            setTimeout(restore, delay);
        } else {
            restore();
        }
    }, []);

    // Clear saved position for a key
    const clearScrollPosition = useCallback((key = 'default') => {
        scrollPositions.current.delete(key);
        try {
            sessionStorage.removeItem(`scroll_${key}`);
        } catch (error) {
            console.warn('Failed to clear scroll position from sessionStorage:', error);
        }
    }, []);

    // Clear all saved positions
    const clearAllScrollPositions = useCallback(() => {
        scrollPositions.current.clear();
        // Clear from sessionStorage
        try {
            const keys = Object.keys(sessionStorage);
            keys.forEach(key => {
                if (key.startsWith('scroll_')) {
                    sessionStorage.removeItem(key);
                }
            });
        } catch (error) {
            console.warn('Failed to clear scroll positions from sessionStorage:', error);
        }
    }, []);

    // Get current scroll position without saving
    const getCurrentScrollPosition = useCallback(() => {
        return {
            x: window.pageXOffset || document.documentElement.scrollLeft,
            y: window.pageYOffset || document.documentElement.scrollTop
        };
    }, []);

    // Check if we have a saved position for a key
    const hasSavedPosition = useCallback((key = 'default') => {
        if (scrollPositions.current.has(key)) {
            return true;
        }
        
        try {
            return sessionStorage.getItem(`scroll_${key}`) !== null;
        } catch (error) {
            return false;
        }
    }, []);

    return {
        saveScrollPosition,
        restoreScrollPosition,
        clearScrollPosition,
        clearAllScrollPositions,
        getCurrentScrollPosition,
        hasSavedPosition
    };
};

export default useScrollPosition;